import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import Add from '@mui/icons-material/Add'
import HelpOutline from '@mui/icons-material/HelpOutline'
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { useGetOrgCredentialType } from '@/service-library/hooks/org-credential-types'
import { useGetOrgCredentials } from '@/service-library/hooks/org-credentials'
import { OrgCredentialType } from '@/types/credentials'
import Breadcrumb from '@/components/breadcrumbs/Breadcrumb'
import AddCredentialDialog from '@/components/credentials/AddCredentialDialog'
import CredentialCard from '@/components/credentials/CredentialCard'
import LargeHeading from '@/components/large-heading/LargeHeading'
import IntegrationSettings from '../integration-settings/IntegrationSettings'
import AppXchangeInstructionsDialog from './AppXchangeInstructionsDialog'
import HowCredentialsWorkDialog from './HowCredentialsWorkDialog'

// Displays a single integration (OrgCredentialType) with its credentials and settings
export default function IntegrationPage() {
  const { id } = useParams() // id of OrgCredentialType
  const integrationTypeId = id as string
  const { isAuthorized: canEditOrgs } = useStytchIsAuthorized('pd.org', 'edit')

  const [selectedCredentialId, setSelectedCredentialId] = useState<
    string | null
  >(null)
  const [showCompanies, setShowCompanies] = useLocalStorage(
    'integration-show-companies',
    true,
  )

  const { orgCredentialType, isPending, isError } = useGetOrgCredentialType({
    id: integrationTypeId,
  })

  const { orgCredentials, queryKey } = useGetOrgCredentials({
    filters: {
      limit: '1000',
      type_id: integrationTypeId,
      fields__include: 'org',
    },
    refetchOnWindowFocus: true,
  })

  const selectedCredential = orgCredentials.find(
    (credential) => credential.id === selectedCredentialId,
  )

  const addCredentialOverlay = useOverlay()
  const howCredentialsWorkOverlay = useOverlay()
  const setupInstructionsOverlay = useOverlay()

  useEffect(() => {
    if (!selectedCredential && orgCredentials.length > 0) {
      setSelectedCredentialId(orgCredentials[0].id)
      return
    }

    if (
      selectedCredential &&
      !orgCredentials.some(
        (credential) => credential.id === selectedCredential.id,
      )
    ) {
      setSelectedCredentialId(orgCredentials[0]?.id || null)
    }
  }, [orgCredentials, selectedCredential])

  const addEnabled =
    orgCredentialType?.supports.companies !== 'required' ||
    orgCredentials.length === 0

  return (
    <Container maxWidth="lg" sx={{ pb: 4 }}>
      {orgCredentialType && (
        <Breadcrumb
          label={orgCredentialType.name}
          url={`/settings/integrations/${orgCredentialType.id}`}
        />
      )}

      {(isError || (!isPending && !orgCredentialType)) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load this integration. You may not have access or it does
          not exist.
        </Alert>
      )}

      {isPending ? (
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress />
        </Stack>
      ) : (
        orgCredentialType && (
          <Stack spacing={4}>
            <LargeHeading
              heading={orgCredentialType.name}
              subHeading="Integration"
            />

            {/* Credentials Section */}
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h5">Credentials</Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                  {orgCredentials.length > 0 &&
                    orgCredentialType.auth_type.includes('app_xchange') && (
                      <Button
                        onClick={setupInstructionsOverlay.open}
                        variant="text"
                      >
                        Setup Instructions
                      </Button>
                    )}
                  <Button
                    startIcon={<HelpOutline />}
                    variant="text"
                    color="neutral"
                    onClick={howCredentialsWorkOverlay.open}
                  >
                    How Credentials Work
                  </Button>
                  {canEditOrgs && (
                    <Tooltip
                      title={
                        addEnabled
                          ? ''
                          : 'This integration only supports one credential.'
                      }
                    >
                      <div>
                        <Button
                          onClick={addCredentialOverlay.open}
                          variant="text"
                          startIcon={<Add />}
                          disabled={!addEnabled}
                        >
                          New Credential
                        </Button>
                      </div>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              <Stack spacing={2}>
                {orgCredentials.map((credential) => (
                  <CredentialCard
                    key={credential.id}
                    credential={credential}
                    listQueryKey={queryKey}
                    type={orgCredentialType as OrgCredentialType}
                    onClick={(c) => setSelectedCredentialId(c.id)}
                    selected={selectedCredential?.id === credential.id}
                  />
                ))}
                {orgCredentials.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Add a credential to get started with this integration.
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* Settings Section */}
            {orgCredentials.length > 0 && selectedCredential && (
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Stack>
                    <Typography variant="h5">Settings</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {orgCredentials.length > 1 &&
                        selectedCredential.org?.name}
                    </Typography>
                  </Stack>
                  {orgCredentialType.supports.companies === 'required' && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowCompanies((visible) => !visible)}
                    >
                      {showCompanies ? 'Hide' : 'Show'} Companies
                    </Button>
                  )}
                </Stack>
                <IntegrationSettings
                  type={orgCredentialType}
                  selectedCredential={selectedCredential}
                  showScopeList={showCompanies}
                />
              </Stack>
            )}

            {canEditOrgs && (
              <AddCredentialDialog
                overlay={addCredentialOverlay}
                orgCredentials={orgCredentials}
                listQueryKey={queryKey}
                type={orgCredentialType}
              />
            )}
          </Stack>
        )
      )}

      <HowCredentialsWorkDialog overlay={howCredentialsWorkOverlay} />
      {orgCredentialType?.auth_type.includes('app_xchange') &&
        orgCredentials.length > 0 && (
          <AppXchangeInstructionsDialog
            overlay={setupInstructionsOverlay}
            credentialId={orgCredentials[0].id}
            integrationName={orgCredentialType.name}
          />
        )}
    </Container>
  )
}
